import React, { useEffect, useRef } from "react";
import { motion, useAnimation } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  FiMail, FiPhone, FiMapPin, FiUser, FiCheckCircle, FiClock, FiDownload,
  FiMessageSquare, FiCalendar, FiPieChart, FiUsers, FiFileText, FiHelpCircle, FiMonitor, FiVideo,
  FiSmartphone, FiMap, FiSend, FiActivity,
} from "react-icons/fi";
import {
  faFacebook, faInstagram, faWhatsapp, faLinkedin,
} from "@fortawesome/free-brands-svg-icons";
import axios from "axios";
import utils from "../../utils";

const useOnScreen = (ref, rootMargin = "-50px") => {
  const [isVisible, setIsVisible] = React.useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { rootMargin }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [ref, rootMargin]);

  return isVisible;
};

const Home = () => {
  const controls = useAnimation();
  const sectionRef = useRef();
  const isVisible = useOnScreen(sectionRef);

  useEffect(() => {
    if (isVisible) {
      controls.start("visible");
    }
  }, [isVisible, controls]);

  useEffect(() => {
    fetchTimbratura();
  }, []);

  const fetchTimbratura = async () => {
    try {
      const res = await axios.get(`/api/timbratura`, { headers: utils.getDipendenteAuthHeaders(), validateStatus: () => true });
      if (res.status < 400) {
        const { success, error } = res.data;
        if (success) {
          window.location.href = '/dip';
        } else {
          console.error(error);
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  const companyDetails = {
    name: "Studio Sonia Romeo",
    emails: [
      "paghe@studiosoniaromeo.it",
    ],
    phone: "045 8201309",
    partitaIVA: "02728160231",
    address: "Via Roveggia, 122/A 37136 Verona VR",
    socialMediaLinks: [
      { icon: faFacebook, url: "https://www.facebook.com/studiosoniaromeo", color: "#1877F2" },
      { icon: faLinkedin, url: "https://www.linkedin.com/in/studio-romeo-31a2a5281/", color: "#0A66C2" },
      { icon: faInstagram, url: "https://www.instagram.com/studiosoniaromeo", color: "#E1306C" },
      { icon: faWhatsapp, url: "https://wa.me/390458201309", color: "#25D366" },
    ],
  };

  const services = [
    {
      title: "Contrattualistica",
      description: "Gestione completa del personale online",
      features: [
        { icon: <FiCheckCircle />, text: "Assunzioni/Cessazioni" },
        { icon: <FiClock />, text: "Proroghe/Trasformazioni" },
        { icon: <FiMessageSquare />, text: "Chiamate Intermittenti" },
        { icon: <FiDownload />, text: "Infortunio/Distacchi" },
        { icon: <FiFileText />, text: "Unilav Subito Pronto" }
      ],
      imageSrc: "/img/service1.webp",
      altText: "Servizio di contrattualistica"
    },
    {
      title: "Buste Paga e Dipendenti",
      description: "Tutte le informazioni in un click",
      features: [
        { icon: <FiUsers />, text: "Lista dipendenti sempre aggiornata" },
        { icon: <FiFileText />, text: "Buste paga per i dipendente" },
        { icon: <FiHelpCircle />, text: "Malattia/Cassa integrazione" },
        { icon: <FiDownload />, text: "Moduli e Documenti" },
        { icon: <FiClock />, text: "Presenze collegate al Timbratore" }
      ],
      imageSrc: "/img/service4.webp",
      altText: "Servizio di gestione buste paga"
    },
    {
      title: "Comunicazioni Enti e Durc",
      description: "Connessione diretta con gli enti",
      features: [
        { icon: <FiFileText />, text: "Richiedi Durc" },
        { icon: <FiDownload />, text: "Edil Connect" },
        { icon: <FiMonitor />, text: "Comunicazioni INPS" },
        { icon: <FiHelpCircle />, text: "Comunicazioni INAIL" },
        { icon: <FiUsers />, text: "Altri Enti Previdenziali" }
      ],
      imageSrc: "/img/service2.webp",
      altText: "Servizio di comunicazioni con enti"
    },
    {
      title: "Timbratore SR",
      description: "Presenze, Eventi, Comunicazioni",
      features: [
        { icon: <FiSmartphone />, text: "App Timbratore Dipendenti" },
        { icon: <FiCalendar />, text: "Richieste Eventi, Ferie, Permessi ecc" },
        { icon: <FiHelpCircle />, text: "Comunicazioni Malattie, Assenze ecc" },
        { icon: <FiFileText />, text: "Cedolini Dipendenti" },
        { icon: <FiMap />, text: "Mappa Posizione Timbrature" }
      ],
      imageSrc: "/img/service5.webp",
      altText: "Servizio di timbratore"
    },
    {
      title: "Previsione Costi",
      description: "Previsioni 24/7 Work in Progress",
      features: [
        { icon: <FiPieChart />, text: "Previsioni disponibili 24/7" },
        { icon: <FiUser />, text: "Consulenza con professionisti" },
        { icon: <FiFileText />, text: "Report dettagliati" },
        { icon: <FiClock />, text: "Aggiornamenti in tempo reale" },
        { icon: <FiActivity />, text: "Assistente AI" }
      ],
      imageSrc: "/img/service6.webp",
      altText: "Servizio di previsione costi"
    },
    {
      title: "Prenotazioni e News",
      description: "Aggiornamenti e notifiche",
      features: [
        { icon: <FiFileText />, text: "News dallo studio con notifiche ed email" },
        { icon: <FiSend />, text: "Chat diretta con lo studio" },
        { icon: <FiCalendar />, text: "Circolari e aggiornamenti" },
        { icon: <FiVideo />, text: "Prenotazione sala riunioni" },
        { icon: <FiDownload />, text: "Invio documenti" }
      ],
      imageSrc: "/img/service3.webp",
      altText: "Servizio di prenotazioni e news"
    }
  ];

  const locationCards = [
    {
      title: "Contatti",
      content: (
        <>
          <div className="contact-item">
            <div className="contact-label">
              <FiMail className="contact-icon" /> <strong>Email:</strong>
            </div>
            <div className="contact-value">
              <a href={`mailto:${companyDetails.emails.join(';')}`} className="contact-link" aria-label="Inviare un'email">
                <i>{companyDetails.emails.join(", ")}</i>
              </a>
            </div>
          </div>
          <div className="contact-item">
            <div className="contact-label">
              <FiPhone className="contact-icon" /> <strong>Telefono:</strong>
            </div>
            <div className="contact-value">
              <a href={`tel:${companyDetails.phone}`} className="contact-link" aria-label="Chiamare">
                {companyDetails.phone}
              </a>
            </div>
          </div>
          <div className="contact-item">
            <div className="contact-label">
              <FiUser className="contact-icon" /> <strong>Partita IVA:</strong>
            </div>
            <div className="contact-value">
              {companyDetails.partitaIVA}
            </div>
          </div>
          <div className="contact-item">
            <div className="contact-label">
              <FiMapPin className="contact-icon" /> <strong>Indirizzo:</strong>
            </div>
            <div className="contact-value">
              {companyDetails.address}
            </div>
          </div>
          <div className="contact-item">
            <div className="contact-label">
              <FiClock className="contact-icon" /> <strong>Orario Apertura:</strong>
            </div>
            <div className="contact-value">
              Lun - Ven Ore 9/13 - 14/17:30
            </div>
          </div>
          <div className="social-media-icons">
            {companyDetails.socialMediaLinks.map((link, index) => (
              <a
                key={index}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="social-icon"
                style={{ color: 'black' }}
                data-hover-color={link.color}
                aria-label={`Visita il nostro profilo su ${link.icon.iconName}`}
              >
                <FontAwesomeIcon icon={link.icon} />
              </a>
            ))}
          </div>
        </>
      ),
    },
    {
      title: "Posizione",
      content: (
        <>
          <div className="map-container">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2294.0022118933143!2d10.962081610931488!3d45.410469636902704!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4781e02a099ad5d3%3A0xd09f581dbb61cc0!2sVia%20Roveggia%2C%20122%2FA%2C%2037136%20Verona%20VR!5e1!3m2!1sit!2sit!4v1704313417547!5m2!1sit!2sit"
              width="100%"
              height="250px"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Google Maps"
            ></iframe>
          </div>
          <p className="map-subtitle">Parcheggio gratuito</p>
        </>
      ),
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  };

  const cardVariants = {
    initial: { scale: 1 },
    hover: {
      scale: 1.03,
      boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
      transition: {
        duration: 0.2,
      },
    },
  };

  const featureVariants = {
    hidden: { x: -20, opacity: 0 },
    visible: (i) => ({
      x: 0,
      opacity: 1,
      transition: {
        delay: i * 0.1,
        duration: 0.3
      }
    })
  };

  return (
    <div className="home-container">
      <section className="video-hero">
        <div className="video-container">
          <div className="video-overlay"></div>
          <video className="video" autoPlay loop muted playsInline>
            <source src="/video.webm" type="video/webm" />
            Il tuo browser non supporta il tag video.
          </video>
          <div className="wave-overlay"></div>
          <motion.div
            className="text-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.5 }}
          >
            <motion.h1
              initial={{ y: -50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3, duration: 1, type: "spring" }}
              className="main-title"
              style={{ fontFamily: "Arial, sans-serif" }}
            >
              Consulente del Lavoro
            </motion.h1>
            <motion.p
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6, duration: 1, type: "spring" }}
              className="studio-name"
            >
              Studio Sonia Romeo
            </motion.p>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 1, type: "spring" }}
              className="cta-button"
              onClick={() => {
                document.querySelector('.services-section').scrollIntoView({ behavior: 'smooth' });
              }}
              aria-label="Scopri i nostri servizi"
            >
              Scopri i Nostri Servizi
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section className="services-section" ref={sectionRef}>
        <motion.div
          className="section-container"
          initial="hidden"
          animate={controls}
          variants={containerVariants}
        >
          <motion.h1 variants={itemVariants} className="section-title">
            Tutto ciò di cui hai bisogno, <span>in un unico posto</span>
          </motion.h1>
          <motion.p variants={itemVariants} className="section-subtitle">
            Gestisci la tua azienda in modo semplice e digitale con i nostri servizi integrati
          </motion.p>

          <motion.div className="services-grid" variants={containerVariants}>
            {services.map((service, index) => (
              <motion.div
                key={index}
                className="service-card"
                variants={itemVariants}
                whileHover="hover"
                initial="initial"
                custom={index}
              >
                <motion.div variants={cardVariants} className="card-inner">
                  {service.imageSrc && (
                    <div className="card-image-container">
                      <img
                        src={service.imageSrc}
                        alt={service.altText}
                        className="card-image"
                        loading="lazy"
                      />
                    </div>
                  )}
                  <div className="card-content">
                    <h3>{service.title}</h3>
                    {service.description && <p className="card-description">{service.description}</p>}
                    {service.features && (
                      <div className="card-features">
                        {service.features.map((feature, i) => (
                          <motion.div
                            key={i}
                            className="feature-item"
                            custom={i}
                            variants={featureVariants}
                          >
                            <span className="feature-icon">{feature.icon}</span>
                            <span>{feature.text}</span>
                          </motion.div>
                        ))}
                      </div>
                    )}
                  </div>
                </motion.div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </section>

      <section className="history-section">
        <motion.div
          className="section-container"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          variants={containerVariants}
        >
          <motion.h1 variants={itemVariants} className="section-title">
            La Nostra <span>Storia</span>
          </motion.h1>
          <motion.p variants={itemVariants} className="section-subtitle">
            Scopri il viaggio che ci ha portato ad arrivare dove siamo
          </motion.p>
          <motion.div className="history-content" variants={itemVariants}>
            <p>
              Lo Studio Sonia Romeo nasce dalla passione e dalla visione della sua fondatrice, Sonia, che vanta oltre vent’anni di esperienza nel settore della consulenza del lavoro. Il desiderio di creare un ambiente in cui professionalità e innovazione si fondano ha dato vita a uno studio all’avanguardia.
            </p>
            <p>
              Fin dal primo giorno, ci siamo posti l’obiettivo di affiancare le aziende nella gestione delle risorse umane, offrendo soluzioni su misura e servizi sempre aggiornati e orientati al futuro.
            </p>
            <p>
              La nostra storia è segnata da un impegno costante verso l’eccellenza: ci distinguiamo per la qualità della consulenza, l’attenzione ai dettagli e la capacità di anticipare i cambiamenti normativi e tecnologici.
            </p>
            <p>
              Oggi, lo Studio Sonia Romeo è un punto di riferimento nel settore, grazie alla fiducia costruita con i clienti e alla preparazione del nostro team di professionisti.
            </p>
            <p>
              Accompagniamo con orgoglio le imprese nel loro percorso di crescita, fornendo strumenti concreti per affrontare le sfide del mondo moderno nel lavoro con competenza, serenità e successo.
            </p>
          </motion.div>
        </motion.div>
      </section>

      <section className="location-section">
        <motion.div
          className="section-container"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={containerVariants}
        >
          <motion.h1 variants={itemVariants} className="section-title" style={{ marginBottom: '1rem', marginTop: '0rem' }}>
            Dove Trovarci
          </motion.h1>
          <motion.p variants={itemVariants} className="section-subtitle">
            Vieni a trovarci per scoprire come possiamo aiutarti
          </motion.p>

          <motion.div className="location-grid" variants={containerVariants}>
            {locationCards.map((card, index) => (
              <motion.div
                key={index}
                className="location-card"
                variants={itemVariants}
                whileHover="hover"
                initial="initial"
                custom={index}
              >
                <motion.div variants={cardVariants} className="card-inner">
                  <div className="card-content">
                    <h3 className={index === 0 ? "contacts-title" : ""}>{card.title}</h3>
                    {card.content}
                  </div>
                </motion.div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </section>

      <style jsx>{`
        .home-container {
          font-family: 'Arial', sans-serif;
          color: #333;
          overflow-x: hidden;
        }

        .video-hero {
          position: relative;
          height: 100vh;
          width: 100%;
          overflow: hidden;
        }

        .video-container {
          position: relative;
          width: 100%;
          height: 100%;
        }

        .video {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .video-overlay {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.4);
        }

        .wave-overlay {
          position: absolute;
          bottom: 0;
          left: 0;
          width: 100%;
          height: 100px;
          background: linear-gradient(
            to bottom,
            rgba(255, 255, 255, 0) 0%,
            rgba(255, 255, 255, 0.8) 70%,
            rgba(255, 255, 255, 1) 100%
          );
          z-index: 2;
        }

        .text-overlay {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          text-align: center;
          color: white;
          width: 100%;
          padding: 0 20px;
          z-index: 3;
        }

        .main-title {
          font-size: 4rem;
          margin-bottom: 1rem;
          text-shadow: 2px 2px 8px rgba(0, 0, 0, 0.8);
          font-weight: 700;
          letter-spacing: 1px;
        }

        .studio-name {
          font-size: 2.5rem;
          text-shadow: 2px 2px 8px rgba(0, 0, 0, 0.8);
          font-family: 'Whisper', cursive;
          margin-bottom: 2rem;
        }

        .cta-button {
          display: inline-block;
          padding: 12px 30px;
          background: rgba(255,255,255,0.2);
          border: 2px solid white;
          border-radius: 50px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          backdrop-filter: blur(5px);
        }

        .cta-button:hover {
          background: rgba(255,255,255,0.3);
          transform: translateY(-3px);
        }

        .services-section, .location-section {
          padding: 4rem 2rem;
          background: white;
          position: relative;
        }

        .section-container {
          max-width: 1200px;
          margin: 0 auto;
        }

        .section-title {
          text-align: center;
          margin-bottom: 1rem;
          font-size: 2.8rem;
          color: #333;
          font-weight: 700;
        }

        .section-title span {
          color: #0066cc;
          font-weight: 700;
        }

        .section-subtitle {
          text-align: center;
          margin-bottom: 3rem;
          font-size: 1.2rem;
          color: #666;
          max-width: 700px;
          margin-left: auto;
          margin-right: auto;
        }

        .services-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 2.5rem;
          padding: 0 1rem;
        }

        .location-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 1rem;
          padding: 0 1rem;
          max-width: 1200px;
          margin: 0 auto;
        }

        .service-card, .location-card {
          perspective: 1000px;
          width: 100%;
        }

        .card-inner {
          background: white;
          border-radius: 15px;
          overflow: hidden;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
          transition: all 0.3s ease;
          height: 100%;
          display: flex;
          flex-direction: column;
        }

        .card-image-container {
          width: 100%;
          height: 200px;
          overflow: hidden;
        }

        .card-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.5s ease;
        }

        .service-card:hover .card-image {
          transform: scale(1.1);
        }

        .card-content {
          padding: 1.8rem;
          flex-grow: 1;
          display: flex;
          flex-direction: column;
        }

        .card-content h3 {
          font-size: 1.5rem;
          margin-bottom: 0rem;
          color: #222;
          font-weight: 600;
          text-align: center;
        }

        .location-card .card-content h3 {
          text-align: center;
          margin-bottom: 1rem;
        }

        .contacts-title {
          text-align: center !important;
          margin-bottom: 1.8rem !important;
        }

        .card-description {
          color: #666;
          font-size: 1rem;
          margin-bottom: 1.5rem;
          font-weight: 500;
          text-align: center;
          font-style: italic;
        }

        .card-features {
          margin-bottom: 1.5rem;
          flex-grow: 1;
        }

        .feature-item {
          display: flex;
          align-items: center;
          margin-bottom: 0.8rem;
          font-size: 0.95rem;
          color: #444;
        }

        .feature-icon {
          margin-right: 10px;
          color: #0066cc;
          font-size: 1.1rem;
        }

        .service-button {
          width: 100%;
          padding: 12px;
          background: #0066cc;
          color: white;
          border: none;
          border-radius: 6px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .service-button:hover {
          background: #0052a3;
          transform: translateY(-2px);
        }

        .history-section {
          padding: 1rem 1rem;
          background: #f9f9f9;
          position: relative;
        }

        .history-content {
          max-width: 1024px;
          margin: 0 auto;
          text-align: center;
          color: #666;
          font-size: 1.1rem;
          line-height: 1.8;
        }

        .history-content p {
          margin-bottom: 1rem;
        }

        .contact-item {
          display: flex;
          flex-direction: row;
          align-items: flex-start;
          margin-bottom: 1rem;
          line-height: 1.8;
          text-align: left;
        }

        .contact-label {
          display: flex;
          align-items: center;
          margin-right: 6px;
        }

        .contact-icon {
          margin-right: 10px;
          color: #0066cc;
          font-size: 1.1rem;
          flex-shrink: 0;
        }

        .contact-value {
          flex: 1;
        }

        .contact-link {
          color: #0066cc;
          text-decoration: none;
          transition: all 0.3s ease;
          word-break: break-word;
        }

        .contact-link:hover {
          color: #0052a3;
          text-decoration: underline;
        }

        .social-media-icons {
          display: flex;
          gap: 1.5rem;
          margin: 2rem 0;
          justify-content: flex-start;
        }

        .social-icon {
          font-size: 1.8rem;
          transition: all 0.2s ease;
        }

        .social-icon:hover {
          transform: scale(1.2);
        }

        .map-subtitle {
          font-size: 0.8rem;
          text-align: center;
          margin-top: 0.5rem;
          color: #666;
          font-style: italic;
        }

        .social-icon[data-hover-color="#1877F2"]:hover {
          color: #1877F2 !important;
        }

        .social-icon[data-hover-color="#0A66C2"]:hover {
          color: #0A66C2 !important;
        }

        .social-icon[data-hover-color="#E1306C"]:hover {
          color: #E1306C !important;
        }

        .social-icon[data-hover-color="#25D366"]:hover {
          color: #25D366 !important;
        }

        .map-container {
          width: 100%;
          height: 250px;
          border-radius: 10px;
          overflow: hidden;
          margin-top: 0.5rem;
        }

        @media (max-width: 992px) {
          .location-grid {
            grid-template-columns: 1fr;
            max-width: 600px;
          }
        }

        @media (max-width: 768px) {
          .main-title {
            font-size: 2.5rem;
          }

          .studio-name {
            font-size: 1.8rem;
          }

          .section-title {
            font-size: 2rem;
          }

          .services-grid, .location-grid {
            grid-template-columns: 1fr;
            max-width: 100%;
            margin: 0 auto;
            padding: 0;
          }

          .card-inner {
            margin: 0 8px;
          }

          .card-content {
            padding: 1.5rem;
          }
        }

        @media (max-width: 768px) {
          .contact-item {
             flex-direction: column;
             align-items: flex-start;
             text-align: left;
             margin-bottom: 1rem;
          }

          .contact-label {
            margin-bottom: 0.5rem;
          }

          .contact-value {
            text-align: left;
            width: 100%;
          }

          .contacts-title {
            text-align: center !important;
          }

          .social-media-icons {
            justify-content: center;
            gap: 2rem;
          }

          .social-icon {
            font-size: 2rem;
          }

          .social-icon:hover {
            transform: scale(1.3);
          }
        }

        @media (max-width: 480px) {
          .main-title {
            font-size: 2rem;
          }

          .studio-name {
            font-size: 1.5rem;
          }

          .cta-button {
            padding: 10px 20px;
            font-size: 0.9rem;
          }

          .section-title {
            font-size: 1.8rem;
          }

          .section-subtitle {
            font-size: 1rem;
          }

          .card-content {
            padding: 1.2rem;
          }

          .card-inner {
            margin: 0 5px;
          }

          .map-container {
            height: 250px;
          }
        }
      `}</style>
    </div>
  );
};

export default Home;
